import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import dashboardBg from "../../assets/images/background.jpg";
import appLogoDefault from "../../assets/images/logo.png";
import {
  BarChart3,
  BedDouble,
  BellRing,
  Building2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ShoppingCart,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { organizationApi } from "@/api";
import { useAuth } from "@/context/AuthContext.jsx";
import UserProfileWidget from "@/components/hotel/UserProfileWidget.jsx";
import ScoringPortalsWidget from "@/components/hotel/ScoringPortalsWidget.jsx";
import { resolveScoringAppUrl } from "@/utils/apiBaseUrl.js";
import { HOTEL_PERMISSIONS as P } from "@/constants/hotelPermissions";
import { toast } from "sonner";
import PwaInstallAndUpdatePrompt from "@/components/pwa/PwaInstallAndUpdatePrompt.jsx";
import NotificationPermissionPrompt from "@/components/alerts/NotificationPermissionPrompt.jsx";
import { PushPermissionModal } from "@/components/alerts/PushPermissionModal.jsx";
import HotelAlertProvider from "@/providers/HotelAlertProvider.jsx";
import OrganizationSwitcher from "@/components/common/OrganizationSwitcher.jsx";
import { buildHotelNavItems } from "./dashboardNavItems";

const getApiData = (response) => {
  return response?.data?.data || response?.data?.Data || response?.data || null;
};

const getSafeUrl = (value) => {
  if (!value || typeof value !== "string") return "";

  const cleanValue = value.trim();

  if (!cleanValue || cleanValue.toLowerCase() === "string") {
    return "";
  }

  return cleanValue;
};

const normalizePath = (path = "") => String(path).replace(/\/+$/, "");

const isRouteMatch = (currentPath, itemPath) => {
  const current = normalizePath(currentPath);
  const item = normalizePath(itemPath);

  if (item === "/hotel/requests" && (current === "/hotel/service-requests" || current.startsWith("/hotel/service-requests/"))) {
    return true;
  }
  if (item === "/hotel/service-requests" && (current === "/hotel/requests" || current.startsWith("/hotel/requests/"))) {
    return true;
  }

  return current === item || current.startsWith(`${item}/`);
};

const getActiveChildPath = (children = [], currentPath) => {
  const matchedChildren = children
    .filter(
      (child) =>
        !child.noActiveHighlight && isRouteMatch(currentPath, child.path),
    )
    .sort((a, b) => b.path.length - a.path.length);

  return matchedChildren[0]?.path || "";
};

const DashboardLayout = () => {
  const location = useLocation();

  const {
    loading,
    logout,
    accessToken,
    isAuthenticated,
    userId,

    userFirstName,
    email,
    userRole,

    organizationName,
    organizationId,
    organizationCode,
    organizationType,
    organizationCapabilities,
    orgImageUrl,
    appLogo,
    backgroundImage,
    isSetupCompleted,
    hasPermission,
    hasScoringAccess,
  } = useAuth();

  const handleGoToScoringApp = useCallback(() => {
    const targetUrl = resolveScoringAppUrl(accessToken || "");
    window.open(targetUrl, "_blank");
  }, [accessToken]);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("hotel_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("hotel_sidebar_collapsed", String(next));
      } catch {}
      return next;
    });
  }, []);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({
    frontDesk:
      location.pathname.startsWith("/hotel/check-availability") ||
      location.pathname.startsWith("/hotel/sessions") ||
      location.pathname.startsWith("/hotel/qr-generator") ||
      location.pathname.startsWith("/hotel/requests") ||
      location.pathname.startsWith("/hotel/service-requests") ||
      location.pathname.startsWith("/hotel/housekeeping") ||
      location.pathname.startsWith("/hotel/room-maintenance"),

    callsAndAlerts:
      location.pathname.startsWith("/hotel/calls") ||
      location.pathname.startsWith("/hotel/contact-links"),

    operations:
      location.pathname.startsWith("/hotel/requests") ||
      location.pathname.startsWith("/hotel/service-requests") ||
      location.pathname.startsWith("/hotel/calls") ||
      location.pathname.startsWith("/hotel/sessions") ||
      location.pathname.startsWith("/hotel/qr-generator") ||
      location.pathname.startsWith("/hotel/services") ||
      location.pathname.startsWith("/hotel/surveys") ||
      location.pathname.startsWith("/hotel/recommended-activities"),

    guestCrm:
      location.pathname.startsWith("/hotel/guests") ||
      location.pathname.startsWith("/hotel/know-your-guest") ||
      location.pathname.startsWith("/hotel/loyalty") ||
      location.pathname.startsWith("/hotel/memberships") ||
      location.pathname.startsWith("/hotel/membership-events") ||
      location.pathname.startsWith("/hotel/coupons") ||
      location.pathname.startsWith("/hotel/partner-redemptions"),

    bookers: location.pathname.startsWith("/hotel/bookers"),

    reservations:
      location.pathname.startsWith("/hotel/bookings") ||
      location.pathname.startsWith("/hotel/bookers") ||
      location.pathname.startsWith("/hotel/booker-rewards") ||
      location.pathname.startsWith("/hotel/check-availability"),

    rooms:
      location.pathname.startsWith("/hotel/rooms") ||
      location.pathname.startsWith("/hotel/room-types") ||
      location.pathname.startsWith("/hotel/room-access-qr"),

    pos: location.pathname.startsWith("/hotel/pos"),

    finance:
      location.pathname.startsWith("/hotel/billing") ||
      location.pathname.startsWith("/hotel/night-audit") ||
      location.pathname.startsWith("/hotel/finance"),

    amenities:
      location.pathname.startsWith("/hotel/services") ||
      location.pathname.startsWith("/hotel/partner-services") ||
      location.pathname.startsWith("/hotel/recommended-activities"),

    configuration:
      location.pathname.startsWith("/hotel/rooms") ||
      location.pathname.startsWith("/hotel/room-types") ||
      location.pathname.startsWith("/hotel/room-access-qr") ||
      location.pathname.startsWith("/hotel/admin") ||
      location.pathname.startsWith("/hotel/settings"),

    userManagement: location.pathname.startsWith("/hotel/admin"),
  });

  const [organizationProfile, setOrganizationProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const UNSEEN_PENDING_KEY = "hotelDashboardUnseenPendingRequests";
  const [unseenPending, setUnseenPending] = useState(() =>
    Number(localStorage.getItem(UNSEEN_PENDING_KEY) || 0),
  );
  const [hubConnected, setHubConnected] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(() =>
    typeof window !== "undefined" && "Notification" in window
      ? window.Notification.permission
      : "unsupported",
  );
  const [showPushModal, setShowPushModal] = useState(false);

  const dashboardBackgroundImage = dashboardBg;
  const callDeskAuthReady = Boolean(
    !loading && isAuthenticated && accessToken && userId && organizationId,
  );

  const navItems = useMemo(
    () =>
      buildHotelNavItems({
        hasPermission,
        organizationCapabilities,
      }),
    [hasPermission, organizationCapabilities],
  );

  const loadOrganizationProfile = async () => {
    try {
      setProfileLoading(true);

      const response = await organizationApi.getOrganizationProfile();
      const data = getApiData(response);

      setOrganizationProfile(data);
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 403) {
        setOrganizationProfile(null);
      } else {
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.Message ||
            "Failed to load organization profile.",
        );
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const clearPendingNotifications = () => {
    localStorage.setItem(UNSEEN_PENDING_KEY, "0");
    setUnseenPending(0);
  };

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) return;

    try {
      const permission = await window.Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === "granted") {
        toast.success("Notification and call permissions granted!");
        localStorage.setItem("hotelDepartmentAlertSound", "true");
        localStorage.setItem("hotelDepartmentAlertVibration", "true");
        localStorage.setItem("hotelDepartmentUrgentModal", "true");
      } else if (permission === "denied") {
        toast.error("Notifications are currently blocked in browser settings.");
      }
    } catch (error) {
      console.error("Notification permission error:", error);
    }
  };

  // Auto-request permission on dashboard load if permission is "default" or "denied"
  useEffect(() => {
    if (callDeskAuthReady && typeof window !== "undefined" && "Notification" in window) {
      const currentPerm = window.Notification.permission;
      if (currentPerm === "default" || currentPerm === "denied") {
        // Only show if we haven't already dismissed it this session to avoid infinite annoyance
        if (!sessionStorage.getItem("pushModalDismissed")) {
          setShowPushModal(true);
        }
      }
    }
  }, [callDeskAuthReady]);

  const handlePushModalClose = (open) => {
    setShowPushModal(open);
    if (!open) {
      sessionStorage.setItem("pushModalDismissed", "true");
    }
  };

  useEffect(() => {
    const path = location.pathname;

    setOpenGroups((prev) => ({
      ...prev,

      frontDesk:
        prev.frontDesk ||
        path.startsWith("/hotel/check-availability") ||
        path.startsWith("/hotel/sessions") ||
        path.startsWith("/hotel/qr-generator") ||
        path.startsWith("/hotel/requests") ||
        path.startsWith("/hotel/service-requests") ||
        path.startsWith("/hotel/housekeeping") ||
        path.startsWith("/hotel/room-maintenance"),

      callsAndAlerts:
        prev.callsAndAlerts ||
        path.startsWith("/hotel/calls") ||
        path.startsWith("/hotel/contact-links"),

      operations:
        prev.operations ||
        path.startsWith("/hotel/requests") ||
        path.startsWith("/hotel/service-requests") ||
        path.startsWith("/hotel/calls") ||
        path.startsWith("/hotel/sessions") ||
        path.startsWith("/hotel/qr-generator") ||
        path.startsWith("/hotel/services") ||
        path.startsWith("/hotel/surveys") ||
        path.startsWith("/hotel/recommended-activities"),

      guestCrm:
        prev.guestCrm ||
        path.startsWith("/hotel/guests") ||
        path.startsWith("/hotel/know-your-guest") ||
        path.startsWith("/hotel/loyalty") ||
        path.startsWith("/hotel/memberships") ||
        path.startsWith("/hotel/membership-events") ||
        path.startsWith("/hotel/coupons") ||
        path.startsWith("/hotel/partner-redemptions"),

      bookers:
        prev.bookers ||
        path.startsWith("/hotel/bookers") ||
        path.startsWith("/hotel/booker-rewards"),

      reservations:
        prev.reservations ||
        path.startsWith("/hotel/bookings") ||
        path.startsWith("/hotel/bookers") ||
        path.startsWith("/hotel/booker-rewards") ||
        path.startsWith("/hotel/check-availability"),

      rooms:
        prev.rooms ||
        path.startsWith("/hotel/rooms") ||
        path.startsWith("/hotel/room-types") ||
        path.startsWith("/hotel/room-access-qr"),

      pos: prev.pos || path.startsWith("/hotel/pos"),

      finance:
        prev.finance ||
        path.startsWith("/hotel/billing") ||
        path.startsWith("/hotel/night-audit") ||
        path.startsWith("/hotel/finance"),

      amenities:
        prev.amenities ||
        path.startsWith("/hotel/services") ||
        path.startsWith("/hotel/partner-services") ||
        path.startsWith("/hotel/recommended-activities"),

      configuration:
        prev.configuration ||
        path.startsWith("/hotel/rooms") ||
        path.startsWith("/hotel/room-types") ||
        path.startsWith("/hotel/room-access-qr") ||
        path.startsWith("/hotel/admin") ||
        path.startsWith("/hotel/settings"),

      userManagement: prev.userManagement || path.startsWith("/hotel/admin"),
    }));
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && isSetupCompleted && hasPermission(P.settings.view)) {
      loadOrganizationProfile();
    }
  }, [loading, isSetupCompleted, hasPermission, P.settings.view]);

  const markPendingUnseen = useCallback((amount = 1) => {
    setUnseenPending((current) => {
      const next = current + amount;
      localStorage.setItem(UNSEEN_PENDING_KEY, String(next));
      return next;
    });
  }, [UNSEEN_PENDING_KEY]);

  const organization = useMemo(() => {
    const profileImageUrl =
      organizationProfile?.imageUrl ||
      organizationProfile?.ImageUrl ||
      organizationProfile?.logo ||
      organizationProfile?.Logo ||
      "";

    const profileAppLogo =
      organizationProfile?.appLogo ||
      organizationProfile?.AppLogo ||
      organizationProfile?.appLogoFile ||
      organizationProfile?.AppLogoFile ||
      "";

    const profileBackgroundImage =
      organizationProfile?.backgroundImage ||
      organizationProfile?.BackgroundImage ||
      organizationProfile?.backgroundImageFile ||
      organizationProfile?.BackgroundImageFile ||
      "";

    return {
      name: organizationName || "The Hotelier",
      code: organizationCode || "",
      type: organizationType || "Management Portal",

      logo:
        getSafeUrl(appLogo) ||
        getSafeUrl(profileAppLogo) ||
        getSafeUrl(orgImageUrl) ||
        getSafeUrl(profileImageUrl) ||
        "",

      backgroundImage:
        getSafeUrl(backgroundImage) || getSafeUrl(profileBackgroundImage) || "",

      address:
        organizationProfile?.address || organizationProfile?.Address || "",

      contactNumber:
        organizationProfile?.contactNumber ||
        organizationProfile?.ContactNumber ||
        "",

      websiteUrl:
        organizationProfile?.websiteUrl ||
        organizationProfile?.WebsiteUrl ||
        "",
    };
  }, [
    organizationName,
    organizationCode,
    organizationType,
    appLogo,
    orgImageUrl,
    backgroundImage,
    organizationProfile,
  ]);

  const appBackgroundImage =
    organization.backgroundImage || dashboardBackgroundImage;

  const renderLogo = () => {
    const logoSrc = organization.logo || appLogoDefault;
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-sm ring-1 ring-slate-200/80">
        <img
          src={logoSrc}
          alt={organization.name || "Organization"}
          className="h-full w-full object-contain"
        />
      </div>
    );
  };

  const renderNav = (collapsed = false) => {
    return (
      <nav className="flex min-h-0 flex-1 flex-col">
        <div className={cn(
          "min-h-0 flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
          collapsed ? "p-2" : "p-3.5"
        )}>
          <div className="space-y-1.5">
            {navItems.map((item) => {
              const Icon = item.icon;

              if (item.children?.length) {
                const activeChildPath = getActiveChildPath(
                  item.children,
                  location.pathname,
                );

                const isGroupActive =
                  !item.noActiveHighlight && Boolean(activeChildPath);

                const isOpen = Boolean(openGroups[item.key] ?? isGroupActive);

                if (collapsed) {
                  return (
                    <Popover key={item.key}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          title={item.label}
                          aria-label={item.label}
                          className={cn(
                            "relative group flex h-12 w-12 mx-auto items-center justify-center rounded-2xl border border-transparent transition-all duration-150 active:scale-95",
                            "hover:border-slate-200/80 hover:bg-slate-100/80 hover:text-slate-950",
                            isGroupActive
                              ? "border-primary/20 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/5 text-primary font-bold shadow-2xs"
                              : "text-slate-600",
                          )}
                        >
                          <Icon className="h-5 w-5 drop-shadow-2xs" />
                          <span
                            className={cn(
                              "absolute bottom-1 right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full transition-all duration-150",
                              isGroupActive
                                ? "bg-primary/20 text-primary"
                                : "bg-slate-100 text-slate-400 group-hover:bg-primary/10 group-hover:text-primary",
                            )}
                          >
                            <ChevronRight className="h-2 w-2 stroke-[3]" />
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="right"
                        align="start"
                        sideOffset={10}
                        className="w-52 p-2 rounded-2xl border-slate-200 shadow-xl bg-white/95 backdrop-blur-md space-y-1 z-50"
                      >
                        <div className="px-2.5 py-1 text-xs font-bold text-slate-900 border-b border-slate-100 mb-1 flex items-center justify-between">
                          <span>{item.label}</span>
                          <span className="text-[10px] font-normal text-muted-foreground">{item.children.length} items</span>
                        </div>
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildActive = activeChildPath === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={cn(
                                "flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-xs font-semibold transition-all duration-150",
                                isChildActive
                                  ? "bg-primary text-white shadow-2xs font-bold"
                                  : "text-slate-700 hover:bg-slate-100 hover:text-slate-950",
                              )}
                            >
                              <ChildIcon className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{child.label}</span>
                            </Link>
                          );
                        })}
                      </PopoverContent>
                    </Popover>
                  );
                }

                return (
                  <div key={item.key} className="space-y-1">
                    <Button
                      type="button"
                      variant="ghost"
                      className={cn(
                        "group flex h-11 w-full items-center justify-between rounded-2xl border border-transparent pl-1.5 pr-3 py-1.5 text-slate-700 transition-all duration-200 font-semibold",
                        "hover:border-slate-200/80 hover:bg-slate-100/80 hover:text-slate-950",
                        isGroupActive &&
                          "border-primary/20 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/5 text-primary font-bold shadow-2xs",
                      )}
                      onClick={() =>
                        setOpenGroups((prev) => ({
                          ...prev,
                          [item.key]: !isOpen,
                        }))
                      }
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <div
                          className={cn(
                            "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-all duration-200",
                            isGroupActive
                              ? "bg-primary text-white shadow-xs shadow-primary/30"
                              : "bg-slate-100/90 text-slate-600 group-hover:bg-slate-200/80 group-hover:text-slate-900 group-hover:scale-105",
                          )}
                        >
                          <Icon className="h-4 w-4 drop-shadow-2xs" />
                        </div>
                        <span className="truncate text-sm">{item.label}</span>
                      </span>

                      <ChevronDown
                        className={cn(
                          "h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:text-slate-700",
                          isOpen && "rotate-180 text-primary",
                        )}
                      />
                    </Button>

                    {isOpen && (
                      <div className="ml-5 space-y-1 border-l-2 border-primary/25 pl-3 py-0.5">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildActive =
                            activeChildPath === child.path;

                          return (
                            <Button
                              key={child.path}
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "group flex h-9 w-full items-center justify-start rounded-xl border border-transparent pl-1.5 pr-2.5 text-xs font-semibold transition-all duration-200",
                                "hover:border-slate-200/80 hover:bg-slate-100/80 hover:text-slate-950",
                                isChildActive &&
                                  "border-primary/20 bg-gradient-to-r from-primary/15 to-primary/5 text-primary font-bold shadow-2xs",
                              )}
                              asChild
                              onClick={() => setMobileOpen(false)}
                            >
                              <Link to={child.path} className="flex items-center gap-2 w-full">
                                <div
                                  className={cn(
                                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-all",
                                    isChildActive
                                      ? "bg-primary text-white shadow-2xs"
                                      : "bg-slate-100 text-slate-500 group-hover:bg-slate-200 group-hover:text-slate-800",
                                  )}
                                >
                                  <ChildIcon className="h-3.5 w-3.5" />
                                </div>
                                <span className="truncate">{child.label}</span>
                              </Link>
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive =
                !item.noActiveHighlight &&
                isRouteMatch(location.pathname, item.path);

              if (collapsed) {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={item.label}
                    aria-label={item.label}
                    className={cn(
                      "relative group flex h-12 w-12 mx-auto items-center justify-center rounded-2xl border border-transparent transition-all duration-150 active:scale-95",
                      "hover:border-slate-200/80 hover:bg-slate-100/80 hover:text-slate-950",
                      isActive
                        ? "border-primary/20 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/5 text-primary font-bold shadow-2xs"
                        : "text-slate-600",
                    )}
                  >
                    <Icon className="h-5 w-5 drop-shadow-2xs" />
                  </Link>
                );
              }

              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  className={cn(
                    "group flex h-11 w-full items-center justify-start gap-2.5 rounded-2xl border border-transparent pl-1.5 pr-3 py-1.5 text-slate-700 transition-all duration-200 font-semibold",
                    "hover:border-slate-200/80 hover:bg-slate-100/80 hover:text-slate-950",
                    isActive &&
                      "border-primary/20 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/5 text-primary font-bold shadow-2xs",
                  )}
                  asChild
                  onClick={() => setMobileOpen(false)}
                >
                  <Link to={item.path} className="flex items-center gap-2.5 w-full">
                    <div
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-all duration-200",
                        isActive
                          ? "bg-primary text-white shadow-xs shadow-primary/30"
                          : "bg-slate-100/90 text-slate-600 group-hover:bg-slate-200/80 group-hover:text-slate-900 group-hover:scale-105",
                      )}
                    >
                      <Icon className="h-4 w-4 drop-shadow-2xs" />
                    </div>
                    <span className="truncate text-sm">{item.label}</span>
                  </Link>
                </Button>
              );
            })}
          </div>
        </div>

        {hasScoringAccess && (
          <div className={cn("shrink-0 pt-2", collapsed ? "px-1 flex justify-center" : "px-3")}>
            <ScoringPortalsWidget compact={collapsed} onLinkClick={() => setMobileOpen(false)} />
          </div>
        )}

        <div className={cn("shrink-0 border-t border-slate-200", collapsed ? "p-2 flex justify-center" : "p-3")}>
          <UserProfileWidget compact={collapsed} />
        </div>
      </nav>
    );
  };

  const flatNavItems = navItems.flatMap((item) =>
    item.children?.length ? item.children : [item],
  );

  const currentModuleTitle =
    flatNavItems
      .filter(
        (item) =>
          item.path &&
          !item.noActiveHighlight &&
          isRouteMatch(location.pathname, item.path),
      )
      .sort((a, b) => b.path.length - a.path.length)[0]?.label ||
    navItems.find(
      (item) =>
        item.path &&
        !item.noActiveHighlight &&
        isRouteMatch(location.pathname, item.path),
    )?.label ||
    "Dashboard";

  const mobileQuickNav = [
    {
      path: "/hotel/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
    },
    {
      path: "/hotel/calls",
      icon: BellRing,
      label: "Calls",
      count: unseenPending,
      permission: P.calls.view,
    },
    {
      path: "/hotel/pos/orders",
      icon: ShoppingCart,
      label: "Orders",
    },
    {
      path: "/hotel/rooms",
      icon: BedDouble,
      label: "Rooms",
    },
    {
      action: "more",
      icon: Menu,
      label: "More",
    },
  ];

  if (loading) {
    return (
      <div className="flex h-dvh w-full items-center justify-center overflow-hidden bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="hotel-admin-shell relative h-dvh bg-slate-50">
      <HotelAlertProvider
        authContext={{
          authReady: callDeskAuthReady,
          accessToken,
          userId,
          userFirstName,
          email,
          userRole,
          organizationId,
          hasPermission,
        }}
        onHubConnectedChange={setHubConnected}
        onPendingAlert={markPendingUnseen}
      >
        <PwaInstallAndUpdatePrompt />

        <div
          className="pointer-events-none fixed inset-0 z-0"
          style={{
            backgroundImage: `url(${appBackgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: 0.18,
            filter: "saturate(0.75) contrast(0.96) brightness(1.04)",
          }}
        />

        <div className="pointer-events-none fixed inset-0 z-0 bg-[linear-gradient(135deg,rgba(248,250,252,0.98)_0%,rgba(241,245,249,0.92)_50%,rgba(248,250,252,0.96)_100%)]" />
        <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_top_right,rgba(146,53,54,0.12)_0%,transparent_45%)]" />
        <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.08)_0%,transparent_50%)]" />
        <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_top_left,rgba(245,158,11,0.06)_0%,transparent_35%)]" />

        <div className="relative z-10 flex h-dvh w-full min-w-0 overflow-hidden">
        <aside className={cn(
          "hidden h-screen shrink-0 flex-col border-r border-slate-200 bg-white shadow-xs lg:flex",
          isCollapsed ? "w-20" : "w-64"
        )}>
          {/* Desktop sidebar header */}
          <div className={cn("shrink-0 border-b border-slate-200", isCollapsed ? "p-3 flex flex-col items-center gap-2" : "p-3.5")}>
            {isCollapsed ? (
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={toggleSidebar}
                  className="group relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-white p-1 shadow-sm ring-1 ring-slate-200/80 hover:ring-primary/40 active:scale-95 transition-all"
                  title="Expand sidebar"
                  aria-label="Expand sidebar"
                >
                  <img
                    src={organization.logo || appLogoDefault}
                    alt={organization.name || "Organization"}
                    className="h-full w-full object-contain"
                  />
                </button>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  className="h-8 w-8 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 active:scale-95"
                  title="Expand sidebar"
                  aria-label="Expand sidebar"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {renderLogo()}
                <div className="min-w-0 flex-1">
                  <OrganizationSwitcher />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  className="h-8 w-8 shrink-0 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 active:scale-95"
                  title="Collapse sidebar"
                  aria-label="Collapse sidebar"
                >
                  <PanelLeftClose className="h-4 w-4" />
                </Button>
              </div>
            )}

            {profileLoading && !isCollapsed && (
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading profile
              </div>
            )}
          </div>

          {renderNav(isCollapsed)}
        </aside>

        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <Button
              type="button"
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu overlay"
            />

            <aside className="relative z-10 flex h-screen w-72 max-w-[85vw] flex-col border-r border-slate-200 bg-white shadow-2xl">
              <div className="shrink-0 border-b border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    {renderLogo()}
                    <div className="min-w-0 flex-1">
                      <OrganizationSwitcher />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="rounded-xl p-2 text-muted-foreground hover:bg-slate-100 hover:text-foreground"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {renderNav()}
            </aside>
          </div>
        )}

        <div className="flex h-dvh w-full min-w-0 flex-1 flex-col overflow-hidden">
          <header className="mobile-header sticky top-0 z-30 shrink-0 border-b border-slate-200 bg-white py-3 shadow-sm lg:hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="hotel-title-area flex items-center gap-2">
                {renderLogo()}

                <div className="hotel-title-area">
                  <h1 className="truncate text-base font-bold">
                    {organization.name}
                  </h1>

                  <p className="truncate text-xs font-medium text-primary">
                    {currentModuleTitle}
                  </p>
                </div>
              </div>

              <div className="header-actions flex items-center gap-2">
                <UserProfileWidget compact={true} />
                {hasPermission?.(P.calls.view) ? (
                <Link
                  to="/hotel/calls"
                  className="relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm"
                  aria-label="Open alert center"
                >
                  <BellRing className="h-5 w-5" />
                  {unseenPending > 0 ? (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
                      {Math.min(unseenPending, 99)}
                    </span>
                  ) : null}
                </Link>
              ) : null}

              <Button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-muted-foreground shadow-sm hover:bg-slate-100 hover:text-foreground"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
              </div>
            </div>
          </header>

          <main className="hotel-main-content relative min-h-0 flex-1 overflow-y-auto pt-3 sm:p-6 lg:pb-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {notificationPermission !== "granted" && (
              <div className="mb-4">
                <NotificationPermissionPrompt
                  permission={notificationPermission}
                  onRequest={requestNotificationPermission}
                  onCheckStatus={() =>
                    setNotificationPermission(
                      typeof window !== "undefined" && "Notification" in window
                        ? window.Notification.permission
                        : "unsupported",
                    )
                  }
                />
              </div>
            )}
            <div className="relative z-10 w-full min-w-0 bg-transparent p-0 sm:rounded-3xl sm:border sm:border-white/70 sm:bg-white/90 sm:p-6 sm:shadow-[0_20px_70px_rgba(15,23,42,0.08)]">
              <Outlet
                context={{
                  organization,
                  organizationProfile,
                  reloadOrganizationProfile: loadOrganizationProfile,
                  notificationState: {
                    hubConnected,
                    notificationPermission,
                    unseenPending,
                    clearPendingNotifications,
                    requestNotificationPermission,
                  },
                }}
              />
            </div>
          </main>

          {unseenPending > 0 && hasPermission?.(P.calls.view) ? (
            <Link
              to="/hotel/calls"
              className="fixed bottom-24 right-4 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-[0_16px_35px_rgba(146,53,54,0.35)] lg:hidden"
              aria-label="Open urgent alerts"
            >
              <BellRing className="h-6 w-6" />
              <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-red-600 px-1 text-xs font-bold">
                {Math.min(unseenPending, 99)}
              </span>
            </Link>
          ) : null}

          <nav className="mobile-bottom-nav z-40 rounded-3xl border border-slate-200 bg-white/95 p-2 shadow-[0_18px_45px_rgba(15,23,42,0.18)] backdrop-blur lg:hidden">
            <div className="grid grid-cols-5 gap-1">
              {mobileQuickNav
                .filter((item) => !item.permission || hasPermission?.(item.permission))
                .map((item) => {
                const Icon = item.icon;
                const isMore = item.action === "more";
                const isActive = isRouteMatch(location.pathname, item.path);

                const className = cn(
                  "relative flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl px-1 text-[11px] font-semibold text-slate-500 transition",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-slate-100 hover:text-slate-900",
                );

                return isMore ? (
                  <Button
                    key={item.label}
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    className={className}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="max-w-full truncate">{item.label}</span>
                  </Button>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={className}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="max-w-full truncate">{item.label}</span>
                    {item.count > 0 ? (
                      <span className="absolute right-2 top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
                        {Math.min(item.count, 99)}
                      </span>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
        </div>
      </HotelAlertProvider>

      <PushPermissionModal
        open={showPushModal}
        onOpenChange={handlePushModalClose}
        permission={notificationPermission}
        onRequestPermission={requestNotificationPermission}
      />
    </div>
  );
};

export default DashboardLayout;
